import { Injectable } from "@angular/core";
import { DocumentsService, RestService, CommonService } from "../../services/common";
import { AcquisitionMapColors, KhasraLevelMapColors, SaleMapColors, UnitMeasurement } from "../../../appconfig";
import { environment } from "../../../environments/environment";
import { forkJoin, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@Injectable()
export class MapService {

    constructor(private restService: RestService, private docService: DocumentsService, private commonService: CommonService) { }

    /**
     * Creates KML File string from a KML file by URL
     * @param url url of KML File whose KML string has to be created
     * @returns Returns KML String
     */
    getKmlFileStringFromUrl(url: string) {
        return this.restService.getPlainFileContentFromUrl(url)
    }

    /**
     * Creates KMZ File string from a KMZ file by URL
     * @param url url of KMZ File whose KMZ location data and image path has to be extracted
     * @returns Returns KMZ File location and image data String
     */
    getKMZFileImageOverlay(url: string) {
        return this.restService.getData(url)
    }

    /**
     * Gets the KML String from a locally uploaded KML File
     * @param file KML file uploaded manually
     * @returns Returns KML file content as a Promise
     */
    getKmlFileFromLocalFile(file: any): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            if (!file) {
                reject("No file provided");
                return;
            }
            reader.onload = (e: any) => {
                resolve(e.target.result);
            };
            reader.onerror = (e) => {
                reject(e);
            }
            reader.readAsText(file);
        });
    }

    /**
     * Repairs/Modifies the KML string so that document tag can be parsed
     * @param kmlstring kml string to be repaired
     * @returns returns repaired kml string with proper document tag
     */
    repairKMLString(kmlstring: string) {
        if (kmlstring.length > 0) {
            var docIndex = kmlstring.indexOf('<Document ');
            if (docIndex > 0) {
                var docIndexStart = kmlstring.indexOf('Document');
                var docIndexLast = kmlstring.indexOf('>', docIndexStart + 1);
                var newStr = kmlstring.substring(docIndexStart + 8, docIndexLast);
                if (newStr)
                    kmlstring = kmlstring.replace(newStr, '');
            }
        }
        return kmlstring;
    }

    /**
     * Calculates the total area of all polygons in a KML file
     * @param file KML File
     * @returns Object containing area and unitMeasurementId
     */
    async calculateKmlArea(file: File): Promise<{ area: number, unitMeasurementId: number }> {
        let kmlString = await this.getKmlFileFromLocalFile(file);
        kmlString = this.repairKMLString(kmlString);
        const xmlDoc = this.parseKml(kmlString, file.name);
        const placemarks = xmlDoc?.querySelectorAll('Placemark');

        let totalSqMeters = 0;
        if (placemarks) {
            placemarks.forEach((placemark: any) => {
                const polygon = this.getPlacemarkPolygon(placemark);
                if (polygon) {
                    const path = this.getPolygoneCordinatesPath(polygon);
                    if (path && path.length > 0) {
                        const area = google.maps.geometry.spherical.computeArea(path);
                        totalSqMeters += area;
                    }
                }
            });
        }

        const areaInBiswa = this.commonService.convertArea(totalSqMeters, UnitMeasurement.SqMeter, UnitMeasurement.Biswa);
        return { area: areaInBiswa, unitMeasurementId: UnitMeasurement.Biswa };
    }

    /**
     * Parse a KML string into xml doc
     * @param kmlString KML string to parse
     * @returns Returns XML Doc parsed from KML String
     */
    parseKml(kmlString: string, kmlUrl: string) {
        // Parse KML string into an XML document
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(kmlString, 'application/xml');
        // const polygon = xmlDoc?.querySelectorAll('Polygon');
        // Check for parse errors
        const parseError = xmlDoc.querySelector('parsererror');
        if (parseError) {
            console.error('Error parsing KML:', kmlUrl + "\n" + parseError.textContent);
            return;
        } else {
            return xmlDoc
        }
    }

    /**
     * Set the Polygon color based on it's status and availability
     * @param polygondata Unit(plot/flat/khasra) Data to find if polygon exist or not of that unit
     * @param searchValue Name of current Polygone which needs to be searched in polygine data
     * @returns Return a polygon to set on map with appropriate color
     */
    searchPolygon(polygondata: any, polygoneName: string, mapData: any) {
        var polygon = null;
        //First check if polygon data has records
        if (polygondata) {
            if (mapData?.kmlFor.toLowerCase() == 'sales' || mapData?.kmlFor.toLowerCase() == 'inventory') {
                //ittrate for each record and search if current polygone name exists for any unit
                for (var index = 0; index < polygondata.length; index++) {
                    //if unit has block as well then add name with block
                    if (polygondata[index]?.block != null && polygondata[index]?.block != undefined && polygondata[index]?.block != '') {
                        var NameWithHyphen = polygondata[index]?.block?.toLowerCase() + '-' + polygondata[index]?.name?.toLowerCase()
                        var NameWithUnderScore = polygondata[index]?.block?.toLowerCase() + '_' + polygondata[index]?.name?.toLowerCase()

                        if (polygoneName?.toLowerCase() == NameWithHyphen || polygoneName?.toLowerCase() == NameWithUnderScore) {
                            //if found the polygon name in data then return that record
                            polygon = polygondata[index]
                            return polygon
                        }
                    }
                    else {
                        //if unit does not contains block information then match the plot no only
                        if (polygoneName?.toLowerCase() == polygondata[index]?.name?.toLowerCase()) {
                            polygon = polygondata[index]
                            return polygon
                        }
                    }
                }
            } else if (mapData?.kmlFor.toLowerCase() == 'acquisition') {
                //if current map level is being used for navigation at country state or district or tehsil level
                //then filter data of current state/district or tehsil
                if (this.isCurrentLevelforNavigation(mapData?.mapLevel)) {
                    if (polygondata?.length > 0 && (polygoneName != '' || name != undefined)) {
                        return polygondata.filter((x: any) => x.name.toLowerCase() === polygoneName?.toLowerCase())
                    }
                }
                else if (mapData?.mapLevel?.toLowerCase() == 'khasra') {
                    //if map level is khasra then check if current khasra data is available or not in main or adjacent khasra
                    if (polygondata?.plotNo.toLowerCase() == polygoneName?.toLowerCase()) {
                        //means current polygon is main khasra
                        polygon = polygondata
                        return polygon;
                    }
                    else if (polygondata?.adjacentLandKhasra) {
                        //if current polygon is not main khasra then check in adjacent khasra
                        for (var index = 0; index < polygondata?.adjacentLandKhasra?.length; index++) {
                            if (polygondata?.adjacentLandKhasra[index]?.plotNo?.toLowerCase() == polygoneName?.toLowerCase()) {
                                polygon = polygondata?.adjacentLandKhasra[index]
                                return polygon;
                            }
                        }
                    }
                }
                else {
                    //ittrate for each record and search if current polygone name exists for any unit
                    // for (var index = 0; index < polygondata?.length; index++) {
                    var foundPolygon = polygondata.find((p: any) => p?.name?.toLowerCase() == polygoneName?.toLowerCase() ||
                        p?.plotNo?.toLowerCase() == polygoneName?.toLowerCase())
                    if (foundPolygon != undefined && foundPolygon != null) {
                        polygon = foundPolygon
                    }
                    // }
                }
            }
        }
        return polygon;
    }

    searchKhasraPolygon(polygonData: any) {
        var polygon = null;
        if (polygonData) {

        }
    }

    /**
     * Gets the polygon from a placemark
     * @param placemark placemark from which polygon has to be fetched
     */
    getPlacemarkPolygon(placemark: any) {
        return placemark.querySelector('Polygon')
    }

    /**
     * Gets path of a Polygon's cordinates
     * @param polygon Polygon whose cordinate's path has to be extracted
     */
    getPolygoneCordinatesPath(polygon: any) {
        //fetch cordinates of current polygon
        const cordinates = polygon.querySelectorAll('coordinates')[0]?.textContent.trim();

        // Convert coordinates to lat/lng and filter out any invalid mappings (like empty strings parsing to 0)
        const path = cordinates.split(/\s+/).map((coord: any) => {
            if (!coord) return null;
            const [lng, lat] = coord.split(',').map(Number);
            return (lat !== undefined && !isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null;
        }).filter((coord: any) => coord !== null);

        return path;
    }

    /**
     * Gets color Theme color for Sale Map
     * @param polygonStatus Status of polygon (found/not found/NA etc.)
     * @param mapData Map data containing kmlFor property to decide if map is for sale or acquisition
     * @param currentKhasraData Current khasra data to decide the color of polygon (khasra level map)
     * @returns Returns color for polygon to be set on map
     */
    GetPolygonColorforMap(polygonRecord: any, mapData: any, currentKhasraData: any, currentPolygoneName?: string, kmlUrl?: string) {
        //Default color will assumed as not found
        var color = null;
        if (mapData?.kmlFor?.toLowerCase() == 'sales' || mapData?.kmlFor?.toLowerCase() == 'inventory') {
            //by default color will be set for Not Found
            color = SaleMapColors.NotFound
            //for exploration and projects also we passing kmlFor as 'Inventory' hence if kml type is 
            //not layout then color should be blank. Only in case of kmlLevel ='Operation' and 
            //kmlType as 'Layout' must use notFound color 
            if (kmlUrl?.toLowerCase().indexOf('layout') == -1) {
                color = ''
            }

            if (currentPolygoneName != undefined && currentPolygoneName != null) {
                //if current polygone i park or garden then ot should be shown as greem
                if (currentPolygoneName?.toString().toLowerCase().indexOf('park') >= 0 || currentPolygoneName?.toString().toLowerCase().indexOf('garden') >= 0) {
                    color = SaleMapColors.Garden
                }
                if (currentPolygoneName?.toString().toLowerCase().indexOf('temple') >= 0) {
                    color = SaleMapColors.Temple
                }
                if (currentPolygoneName?.toString().toLowerCase().indexOf('water') >= 0 || currentPolygoneName?.toString().toLowerCase().indexOf('swimming') >= 0) {
                    color = SaleMapColors.Water
                }
                else {
                    //if current record is null means that polygon not found in polygon data array
                    if (polygonRecord != null && polygonRecord != undefined) {
                        //if record exists of that polygon then need to check if that unit is alloted or not to a client by
                        // checking "isAllotted" property of that record and assign colours accordingly
                        if (polygonRecord?.isAllotted)
                            color = SaleMapColors.Allotted
                        else
                            color = SaleMapColors.NotAllotted

                        //if current unit is blocked or marked then it will override the 
                        //allotment color
                        if (polygonRecord?.status.toLowerCase() == 'blocked' || polygonRecord?.status.toLowerCase() == 'mortgage')
                            color = SaleMapColors.NotFound
                    }
                }
            }
        }
        else if (mapData?.kmlFor?.toLowerCase() == 'acquisition') {
            //bydefault color will be for Not Found
            color = AcquisitionMapColors.NotFound
            //if current map level is at country/state,district/or tehsil level then we will show color as per status of 
            //khasra's in that level. Means if at any level a user may have khasras with different status like acquired or 
            //token paid/ interested etc. so will show colour accordingly. hence same level polygon will have multiple status
            if (this.isCurrentLevelforNavigation(mapData?.mapLevel?.toString().toLowerCase())) {
                //We need to get the highest applicable colour based on status. like Acquired status will take precedence ober
                //token paid. hence if there are both the status khasra in this level (state/district) then it will show color of acquired
                color = this.getPrecedenceColor(polygonRecord, currentPolygoneName)
            }
            else {
                //if current record is null means that polygon not found in polygon data array
                if (polygonRecord != null && polygonRecord != undefined) {

                    if (mapData?.mapLevel?.toLowerCase() == 'khasra') {
                        //if map level is khasra then check if current khasra data is available or not
                        if (currentKhasraData != null && currentKhasraData != undefined) {
                            //if record exists of that polygon then need to check if curreny polygone is main khasra or adjacent khasra
                            if (polygonRecord?.plotNo == currentKhasraData.plotNo)
                                //means current polygon is main khasra
                                color = KhasraLevelMapColors.MainKhasra
                            else
                                //means current polygon is adjacent khasra
                                color = KhasraLevelMapColors.AdjacentKhasra
                        }
                    }
                    else {
                        //if record exists of that polygon then need to check the status of  khasra (Prospects/TokenPaid/Purchased)
                        // if (polygonRecord?.status?.toLowerCase() == 'prospects')
                        //     color = AcquisitionMapColors.Prospect
                        // else if (polygonRecord?.status?.toLowerCase() == 'token paid')
                        //     color = AcquisitionMapColors.TokenPaid
                        // else if (polygonRecord?.status?.toLowerCase() == 'purchased')
                        //     color = AcquisitionMapColors.Acquired
                        // else if (polygonRecord?.status?.toLowerCase() == 'ats')
                        //     color = AcquisitionMapColors.ATS
                        // else if (polygonRecord?.status?.toLowerCase() == 'mutation')
                        //     color = AcquisitionMapColors.Mutation
                        // else if (polygonRecord?.status?.toLowerCase() == 'acquired')
                        //     color = AcquisitionMapColors.Acquired
                        // else if (polygonRecord?.status?.toLowerCase() == 'interested')
                        //     color = AcquisitionMapColors.interested
                        // else if (polygonRecord?.status?.toLowerCase() == 'subhash')
                        //     color = AcquisitionMapColors.Subhash
                    }
                }
            }
        }
        return color
    }


    getPrecedenceColor(currentLevelData: any, currentLocationName?: string) {
        var defaultColor = AcquisitionMapColors.NotFound
        // if (currentLocationName?.toLocaleLowerCase() == currentLevelData)
        //     if (currentLevelData.filter((item: any) => item.status?.toLowerCase() === "acquired").length > 0) {
        //         defaultColor = AcquisitionMapColors.Acquired
        //         return defaultColor;
        //     }
        // if (currentLevelData.filter((item: any) => item.status?.toLowerCase() === "partially acquired").length > 0) {
        //     defaultColor = AcquisitionMapColors.PartiallyAcquired
        //     return defaultColor;
        // }
        // if (currentLevelData.filter((item: any) => item.status?.toLowerCase() === "token paid").length > 0) {
        //     defaultColor = AcquisitionMapColors.TokenPaid
        //     return defaultColor;
        // }
        // if (currentLevelData.filter((item: any) => item.status?.toLowerCase() === "ats").length > 0) {
        //     defaultColor = AcquisitionMapColors.ATS
        //     return defaultColor;
        // }
        // if (currentLevelData.filter((item: any) => item.status?.toLowerCase() === "mutation").length > 0) {
        //     defaultColor = AcquisitionMapColors.Mutation
        //     return defaultColor;
        // }
        return defaultColor;
    }

    /**
     * Gets HTML content to show on click of a polygon on map
     * @param kmlFor purpose of kml to decide if map has to be depicted for sale/purchase
     * @param unitStatus Status of current polygon data found or not
     * @returns returns HTML Content of currnt polygon
     */
    getHTMLContentForMap(kmlFor: any, unitStatus: any, isLiveLocation: boolean = false) {
        //Get HTML Content of unit for map as per unit status
        if (kmlFor?.toLowerCase() == 'acquisition') {
            if (isLiveLocation) {
                return this.restService.getPlainFileContentFromUrl('assets/map-html/live/live.html')
            }
            else {
                if (unitStatus != null && unitStatus != undefined)
                    return this.restService.getPlainFileContentFromUrl('assets/map-html/acquisition/acquisition.html')
                else
                    return this.restService.getPlainFileContentFromUrl('assets/map-html/acquisition/not-found.html')
            }
        }
        else
            return this.restService.getPlainFileContentFromUrl('assets/map-html/' + kmlFor?.toLowerCase() + '/' +
                unitStatus?.toLowerCase() + '.html')
    }


    /**
     * Calculates the centroid of the polygon
     * @param path path cordinates of the polygon
     * @returns returns center points (lattitude, longitude) of the polygone
     */
    getCentroid(path: any): { lat: number, lng: number } {
        let centroid = { lat: 0, lng: 0 };

        let x = 0;
        let y = 0;
        let z = 0;

        // Loop through all the coordinates of the polygon
        path.forEach((latLng: any) => {
            const lat = latLng.lat;
            const lon = latLng.lng;

            // Convert lat/lon to Cartesian coordinates
            const latRad = this.toRadians(lat);
            const lonRad = this.toRadians(lon);

            x += Math.cos(latRad) * Math.cos(lonRad);
            y += Math.cos(latRad) * Math.sin(lonRad);
            z += Math.sin(latRad);
        });

        const totalPoints = path?.length
        x /= totalPoints;
        y /= totalPoints;
        z /= totalPoints;

        const lon = Math.atan2(y, x);
        const hyp = Math.sqrt(x * x + y * y);
        const lat = Math.atan2(z, hyp);

        centroid.lat = this.toDegrees(lat);
        centroid.lng = this.toDegrees(lon);

        return centroid;
    }

    private toDegrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    private toRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     * Gets khasra media file timeline based on folder created for different dates
     * @param folderPath Folder path from where khasra media files dates have to be fetched
     */
    getKhasraMediaFileTimeline(folderPath: string) {
        return this.docService.getSubFolderListInFolder(folderPath);
    }

    /**
     * Gets inventory KML URLs along with available layout versions.
     * @param entityType 'Project' or 'Exploration'
     * @param entityId The ID of the project or exploration
     * @param versionNumber (Optional) The specific layout version to fetch
     * @returns Observable containing parentKmlUrls, versionKmlUrls, and layoutVersions
     */
    getInventoryKMLUrlsWithLayoutVersions(entityType: 'Project' | 'Exploration', entityId: number | string, versionNumber?: string): Observable<{ parentKmlUrls: string[], versionKmlUrls: string[], layoutVersions: string[], selectedLayoutVersion: string }> {
        const parentFolder = `KML Files/Dashboard/Inventory/${entityType}/${entityId}`;
        const downloadApiBasePath = `dashboard%2FInventory%2F${entityType}%2F${entityId}`;

        return this.docService.getSubFolderListInFolder(parentFolder).pipe(
            switchMap((versionRes: any) => {
                let layoutVersions: string[] = [];
                if (versionRes && versionRes.result && versionRes.result.length > 0) {
                    layoutVersions = versionRes.result.map((folder: any) => folder.fileName);
                }

                const parentKmlUrls: string[] = [];
                return this.docService.getDocumentListInFolder(parentFolder).pipe(
                    switchMap((parentFileList: any) => {
                        if (parentFileList && parentFileList.result) {
                            parentFileList.result.forEach((currfile: any) => {
                                const fname = currfile.fileName.toLowerCase();
                                if (fname.endsWith('.kml') || fname.endsWith('.kmz')) {
                                    parentKmlUrls.push(
                                        `${environment.apiEndpoint}/api/kml/download-file?filePath=${downloadApiBasePath}%2F${currfile.fileName}&x=${Math.random()}`
                                    );
                                }
                            });
                        }

                        let selectedLayoutVersion = '';
                        if (layoutVersions.length > 0) {
                            selectedLayoutVersion = versionNumber || layoutVersions[0];
                            const versionFolder = `${parentFolder}/${selectedLayoutVersion}`;
                            return this.docService.getDocumentListInFolder(versionFolder).pipe(
                                map((versionFileList: any) => {
                                    const versionKmlUrls: string[] = [];
                                    if (versionFileList && versionFileList.result) {
                                        versionFileList.result.forEach((currfile: any) => {
                                            const fname = currfile.fileName.toLowerCase();
                                            if (fname.endsWith('.kml') || fname.endsWith('.kmz')) {
                                                versionKmlUrls.push(
                                                    `${environment.apiEndpoint}/api/kml/download-file?filePath=${downloadApiBasePath}%2F${selectedLayoutVersion.replace(/ /g, '%20')}%2F${currfile.fileName}&x=${Math.random()}`
                                                );
                                            }
                                        });
                                    }
                                    return { parentKmlUrls, versionKmlUrls, layoutVersions, selectedLayoutVersion };
                                })
                            );
                        } else {
                            return of({ parentKmlUrls, versionKmlUrls: [], layoutVersions, selectedLayoutVersion });
                        }
                    })
                );
            })
        );
    }

    getKMLFileUrls(projectId: any = null, kmlFor: string, kmlLevel: string, callback: (urls: string[]) => void, countryId?: any, stateId?: any, districtId?: any, tehsilId?: any, villageId?: any, khasraId?: any): void {
        let folderPath = '';
        let kmlUrlsBasePath = '';
        let districtFolderPath = ''
        let kmlUrlsDistrictPath = '';
        let khasraFolderPath = ''
        let kmlUrlsKhasraPath = '';
        //Set KML Paths for dashbaord.
        if (kmlFor.toLocaleLowerCase() == 'inventory') {
            folderPath = `KML Files/Dashboard/${kmlFor}/Project/${projectId}/${kmlLevel}`;
            kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2FProject%2F${projectId}%2F${kmlLevel}`;
        }
        else if (kmlFor.toLocaleLowerCase() == 'sales') {
            folderPath = `KML Files/Dashboard/${kmlFor}/Project/${projectId}`;
            kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2FProject%2F${projectId}`;
        }
        else if (kmlFor.toLocaleLowerCase() == 'acquisition') {
            if (kmlLevel.toLowerCase() == 'country') {
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}`;
            }
            if (kmlLevel.toLowerCase() == 'state') {
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}`;
            }
            if (kmlLevel.toLowerCase() == 'district') {
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}`;
            }
            if (kmlLevel.toLowerCase() == 'tehsil') {
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}/tehsil/${tehsilId}`;
                districtFolderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}%2Ftehsil%2F${tehsilId}`;
                kmlUrlsDistrictPath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}`;
            }
            else if (kmlLevel.toLowerCase() == 'village') {
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}/Tehsil/${tehsilId}/Village/${villageId}`;
                districtFolderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}%2FTehsil%2F${tehsilId}%2FVillage%2F${villageId}`;
                kmlUrlsDistrictPath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}`;
            }
            else if (kmlLevel.toLowerCase() == 'khasra') {
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}/Tehsil/${tehsilId}/Village/${villageId}/khasra/${khasraId}`;
                districtFolderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}%2FTehsil%2F${tehsilId}%2FVillage%2F${villageId}/khasra%2F${khasraId}`;
                kmlUrlsDistrictPath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}`;
            }
            else if (kmlLevel.toLowerCase() == 'live') {
                khasraFolderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}/Tehsil/${tehsilId}/Village/${villageId}/khasra/${khasraId}`;
                folderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}/Tehsil/${tehsilId}/Village/${villageId}`;
                // districtFolderPath = `KML Files/Dashboard/${kmlFor}/country/${countryId}/state/${stateId}/district/${districtId}`;

                kmlUrlsKhasraPath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}%2FTehsil%2F${tehsilId}%2FVillage%2F${villageId}/khasra%2F${khasraId}`;
                kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}%2FTehsil%2F${tehsilId}%2FVillage%2F${villageId}`;
                // kmlUrlsDistrictPath = `${environment.apiEndpoint}/api/kml/download-file?filePath=Dashboard%2F${kmlFor}%2Fcountry%2F${countryId}%2Fstate%2F${stateId}%2Fdistrict%2F${districtId}`;
            }
        }

        var kmlUrls: string[] = [];
        if (kmlFor.toLocaleLowerCase() == 'acquisition') {
            //in case of kml fetching for acquisition we need to fetch district level files as well. 
            forkJoin([this.docService.getDocumentListInFolder(khasraFolderPath), this.docService.getDocumentListInFolder(folderPath), this.docService.getDocumentListInFolder(districtFolderPath)]).subscribe({
                next: ([khasraFileList, fileList, districtFileList]) => {
                    kmlUrls = this.addFileListToArray(kmlUrls, khasraFileList, kmlUrlsKhasraPath)
                    kmlUrls = this.addFileListToArray(kmlUrls, fileList, kmlUrlsBasePath)
                    kmlUrls = this.addFileListToArray(kmlUrls, districtFileList, kmlUrlsDistrictPath)
                    callback(kmlUrls);
                },
                error: (err) => {
                    console.error('Failed to fetch document list:', err);
                }
            })
        }
        else {
            this.docService.getDocumentListInFolder(folderPath).subscribe({
                next: (fileList) => {
                    kmlUrls = this.addFileListToArray(kmlUrls, fileList, kmlUrlsBasePath)
                    callback(kmlUrls);
                }, error: (err) => {
                    console.error('Failed to fetch document list:', err);
                }
            })
        }
    }

    addFileListToArray(fileArray: string[], fileListResposne: any, kmlUrlsPath: any): string[] {
        if ((fileListResposne && fileListResposne?.result)) {
            fileListResposne?.result.forEach((file: any) => {
                if (file.fileName.toLowerCase().endsWith('.kml') || file.fileName.toLowerCase().endsWith('.kmz')) {
                    const encodedFileName = encodeURIComponent(file.fileName);
                    fileArray.push(
                        kmlUrlsPath + "%2F" + encodedFileName + '&x=' + Math.random()
                    );
                }
            });
        }
        return fileArray
    }


    getKhasraKMLFileUrls(khasra: any, callback: (urls: string[]) => void): void {
        const countryId = khasra?.village?.tehsil?.district?.state?.country?.id;
        const stateId = khasra?.village?.tehsil?.district?.state?.id;
        const districtId = khasra?.village?.tehsil?.district?.id;
        const tehsilId = khasra?.village?.tehsil?.id;
        const villageId = khasra?.village?.id;

        let kmlUrls: string[] = [];

        const folderPath = `KML Files / Dashboard / Acquisition / Country / ${countryId} /State/${stateId} /District/${districtId} /Tehsil/${tehsilId} /Village/${villageId}/Khasra`;

        const kmlUrlsBasePath = `${environment.apiEndpoint}/api/kml/download-file?filePath=${folderPath.replace('KML Files/', '')}`;

        this.docService.getDocumentListInFolder(folderPath).subscribe({
            next: (fileList) => {
                // Check if fileList and its result are defined
                if (fileList && fileList?.result) {
                    // Find only those files in the list which belongs to adjacent khasra
                    khasra?.adjacentLandKhasra.forEach((adjKhasra: any) => {
                        const adjKhasrafile = fileList?.result.filter((cfile: any) => {
                            return (cfile?.fileName?.toLowerCase() === (adjKhasra?.plotNo + '.kml').toLowerCase() ||
                                cfile?.fileName?.toLowerCase() === (adjKhasra?.plotNo + '.kmz').toLowerCase())
                        })
                        if (adjKhasrafile.length > 0) {
                            const encodedFileName = encodeURIComponent(adjKhasrafile[0]?.fileName);
                            kmlUrls.push(
                                kmlUrlsBasePath + "%2F" + encodedFileName + '&x=' + Math.random()
                            );
                        }
                    });

                    // Also add the current khasra file if it exists
                    const khasraFile = fileList?.result.filter((cfile: any) => {
                        return (cfile?.fileName?.toLowerCase() === (khasra?.plotNo + '.kml').toLowerCase() ||
                            cfile?.fileName?.toLowerCase() === (khasra?.plotNo + '.kmz').toLowerCase())
                    });
                    if (khasraFile.length > 0) {
                        const encodedFileName = encodeURIComponent(khasraFile[0]?.fileName);
                        kmlUrls.push(
                            kmlUrlsBasePath + "%2F" + encodedFileName + '&x=' + Math.random()
                        );
                    }
                }
                callback(kmlUrls);
            },
            error: (err) => {
                console.error('Failed to fetch document list:', err);
            }
        });
    }

    /**
     * Gets the location paths of acquisition files based on khasra data
     * @param khasra khasra data to get the location paths
     * @returns Returns the location paths of acquisition files
     */
    getAcquistionfilesLocationPaths(khasra: any) {
        return `KML Files/Dashboard/Acquisition/Country/${khasra?.village?.tehsil?.district?.state?.country?.id}/State/${khasra?.village?.tehsil?.district?.state?.id}/District/${khasra?.village?.tehsil?.district?.id}/Tehsil/${khasra?.village?.tehsil?.id}/Village/${khasra?.village?.id}`;
    }

    /**
     * Gets khasra media files from the server
     * @param folderPath Folder from where khasra media files have to be fetched
     * @param fileType type of file to be fetched (image/video etc.)
     * @param downloadFile if true then it will download the file, else it will return the list of files in the folder
     * @returns 
     */
    getKhasraMedia(folderPath: string = '', fileType: string, downloadFile: boolean = false) {
        if (downloadFile) {
            // Encode the file name to handle special characters
            // const encodedFileName = encodeURIComponent(`${folderPath}`);
            return this.docService.getDocumentBytesByFolderPath(folderPath);
        }
        else {
            return this.docService.getDocumentListInFolder(folderPath);
        }
    }

    /**
     * Saves a KML file to the server with geographical hierarchy parameters
     * @param kmlFile The KML/KMZ file to upload
     * @param kmlFor The purpose of KML upload (Sales, Acquisition, etc.)
     * @param countryId Optional country ID
     * @param stateId Optional state ID
     * @param districtId Optional district ID
     * @param tehsilId Optional tehsil ID
     * @param villageId Optional village ID
     * @param projectId Optional project ID
     * @param landKhasraId Optional land khasra ID
     * @param kmlType Type of KML (Barren, Stitching, Layout, etc.)
     * @param kmlVersion Version identifier for the KML file
     * @param processKhasras Whether to process khasras from the KML file
     * @returns Observable with response from the server
     */
    saveKml(
        kmlFile: File,
        kmlFor: string,
        countryId?: number,
        stateId?: number,
        districtId?: number,
        tehsilId?: number,
        villageId?: number,
        projectId?: number,
        landKhasraId?: number,
        kmlType?: string,
        kmlVersion?: string,
        processKhasras?: boolean,
        explorationId?: number,
        landArea?: number,
        landAreaUnit?: number
    ) {
        const formData = new FormData();
        formData.append('KMLFile', kmlFile);
        formData.append('KMLFor', kmlFor);

        if (countryId !== undefined && countryId !== null) {
            formData.append('CountryId', countryId.toString());
        }
        if (stateId !== undefined && stateId !== null) {
            formData.append('StateId', stateId.toString());
        }
        if (districtId !== undefined && districtId !== null) {
            formData.append('DistrictId', districtId.toString());
        }
        if (tehsilId !== undefined && tehsilId !== null) {
            formData.append('TehsilId', tehsilId.toString());
        }
        if (villageId !== undefined && villageId !== null) {
            formData.append('VillageId', villageId.toString());
        }
        if (projectId !== undefined && projectId !== null) {
            formData.append('ProjectId', projectId.toString());
        }
        if (explorationId !== undefined && explorationId !== null) {
            formData.append('ExplorationId', explorationId.toString());
        }
        if (landArea !== undefined && landArea !== null) {
            formData.append('LandArea', landArea.toString());
        }
        if (landAreaUnit !== undefined && landAreaUnit !== null) {
            formData.append('LandAreaUnit', landAreaUnit.toString());
        }
        if (landKhasraId !== undefined && landKhasraId !== null) {
            formData.append('LandKhasraId', landKhasraId.toString());
        }
        if (kmlType) {
            formData.append('KmlType', kmlType);
        }
        if (kmlVersion) {
            formData.append('KmlVersion', kmlVersion);
        }
        if (processKhasras !== undefined && processKhasras !== null) {
            formData.append('ProcessKhasras', processKhasras.toString());
        }

        return this.restService.postData('kml/savekml', formData, false, true);
    }


    /**
     * Gets the distinct locations of whose khasras current logged in user has access to at different levels 
     * @param locationLevel level at which we need to know the locations like country, states, disritcts, tehsils, or villages
     */
    getUserKhasraLocations(locationLevel: string) {
        var locations: any;
        var localUrl: any = 'userkhasras';
        var currentUser = localStorage.getItem("userId")
        if (currentUser)
            locations = this.restService.getData(`${localUrl}/location?userId=${currentUser}&locationLevel=${locationLevel}`)

        return locations;
    }

    //if current level of map is for acquistion and for country, state, district, tehsil, and village
    isCurrentLevelforNavigation(currentLevel: string) {
        currentLevel = currentLevel?.toLocaleLowerCase()
        if (currentLevel == "country" || currentLevel == "state" || currentLevel == "district" || currentLevel == "tehsil")
            return true
        else
            return false
    }


    /**
     * Checks weather current location contains the multiple status (in case of acquisition navigation level map only) 
     * @param polygoneData Polygon data array of current level location 
     * @returns returns if it is an interested location or not
     */
    isInterestedLocation(polygoneData: any) {
        //All those locations whose status is "interested/token paid/partially acquired or acquired" are interested location
        if (Array.isArray(polygoneData)) {
            if (polygoneData?.filter((x: any) => x.status?.toLowerCase() === 'interested').length > 0
                || polygoneData?.filter((x: any) => x.status?.toLowerCase() === 'token paid').length >= 0
                || polygoneData?.filter((x: any) => x.status?.toLowerCase() === 'ats').length > 0
                || polygoneData?.filter((x: any) => x.status?.toLowerCase() === 'partially acquired').length > 0
                // || polygoneData?.filter((x: any) => x.status?.toLowerCase() === 'acquired').length > 0
                || polygoneData?.filter((x: any) => x.status?.toLowerCase() === 'mutation').length > 0) {
                return true
            }
        }
        else if (polygoneData?.status?.toLowerCase() == 'interested' || polygoneData?.status?.toLowerCase() == 'partially acquired' ||
            polygoneData?.status?.toLowerCase() == 'ats' || polygoneData?.status?.toLowerCase() == 'token paid' ||
            polygoneData?.status?.toLowerCase() == 'acquired' || polygoneData?.status?.toLowerCase() == 'mutation') {
            return true;
        }
        return false
    }
}
